import { useState, useEffect, useCallback } from "react";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import { useNavigate, useLocation } from "react-router-dom";
import "../../../styles/pages/auditEvidence.css";

export default function AuditEvidence() {
  const { reportingYear, reportingYearId } = useReportingYear();
  const navigate    = useNavigate();
  const location    = useLocation();
  const passedState = location.state || {};

  const [fileData,           setFileData]           = useState([]);
  const [stats,              setStats]              = useState({ evidence_files: 0, linked_sections: 0, total_comments: 0 });
  const [principleDropdown,  setPrincipleDropdown]  = useState([]);
  const [filterSection,      setFilterSection]      = useState("");
  const [searchQuery,        setSearchQuery]        = useState("");
  const [loading,            setLoading]            = useState(true);
  const [error,              setError]              = useState("");
  const [selectedEntity,     setSelectedEntity]     = useState(passedState.selectedEntity || "");
  const [legalEntities,      setLegalEntities]      = useState(passedState.legalEntities  || []);

  // ── Fetch legal entities ───────────────────────────────────────────────────
  useEffect(() => {
    if (passedState.legalEntities?.length > 0) return;
    if (!reportingYearId) return;
    apiClient
      .get("/api/legal-entities-by-year", { params: { reporting_year_id: reportingYearId } })
      .then((res) => setLegalEntities(res.data.code === 200 ? res.data.data : []))
      .catch(() => setLegalEntities([]));
  }, [reportingYearId, passedState.legalEntities]);

  // ── Fetch evidence ─────────────────────────────────────────────────────────
  const fetchEvidence = useCallback(async () => {
    if (!reportingYearId) return;
    setLoading(true);
    setError("");
    try {
      const response = await apiClient.get("/api/audit-evidence", {
        params: {
          reporting_year_id: reportingYearId,
          legal_entity_id:   selectedEntity,
        },
      });
      const data = response.data;
      if (data.success) {
        setStats({
          evidence_files:  data.data.evidence_files  ?? 0,
          linked_sections: data.data.linked_sections ?? 0,
          total_comments:  data.data.total_comments  ?? 0,
        });
        setPrincipleDropdown(data.data.principle_dropdown || []);
        setFileData(data.data.results || []);
      } else {
        setError(data.message || "Failed to load evidence.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load evidence.");
    } finally {
      setLoading(false);
    }
  }, [reportingYearId, selectedEntity]);

  useEffect(() => {
    fetchEvidence();
  }, [fetchEvidence]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const getFileName = (path) => (path ? path.split("/").pop() : "Unknown file");

  const downloadFile = (e, filePath) => {
    e.stopPropagation();
    if (!filePath) return;
    const a    = document.createElement("a");
    a.href     = `https://brsrapi.digioxide.com${filePath}`;
    a.download = getFileName(filePath);
    a.target   = "_blank";
    a.click();
  };

  // ── Filter / Search ────────────────────────────────────────────────────────
  const filteredFiles = fileData.filter((f) => {
    const matchSection = !filterSection || f.principle_code === filterSection;
    const q            = searchQuery.toLowerCase();
    const matchSearch  =
      !q ||
      getFileName(f.evidence_path).toLowerCase().includes(q) ||
      (f.principle_name || "").toLowerCase().includes(q)     ||
      (f.uploader_name  || "").toLowerCase().includes(q);
    return matchSection && matchSearch;
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="audit-container">
      <h2 className="page-title">Audit &amp; Evidence Screen</h2>
      <p className="page-sub">View uploaded evidence linked to BRSR sections with full comment history</p>

      {/* Filter card */}
      <div className="filter-card">
        <div className="filter-group">
          <label>Reporting Year *</label>
          <input className="filter-input" value={reportingYear || ""} disabled />
        </div>
        <div className="filter-group">
          <label>Legal Entity *</label>
          <select
            className="filter-input"
            value={selectedEntity}
            onChange={(e) => setSelectedEntity(e.target.value)}
          >
            <option value="">Select</option>
            {legalEntities.map((e) => (
              <option key={e.entity_id} value={e.entity_id}>{e.entity_name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-title">EVIDENCE FILES</div>
          <div className="stat-value">{loading ? "—" : stats.evidence_files}</div>
        </div>
        <div className="stat-card">
          <div className="stat-title">TOTAL COMMENTS</div>
          <div className="stat-value">{loading ? "—" : stats.total_comments}</div>
        </div>
      </div>

      {/* Error */}
      {error && <div className="error-banner">{error}</div>}

      {/* Filters */}
      <div className="filter-box">
        <div className="filter">
          <label>Section</label>
          <select value={filterSection} onChange={(e) => setFilterSection(e.target.value)}>
            <option value="">All Sections</option>
            {principleDropdown.map((p) => (
              <option key={p.principle_id} value={p.principle_code}>
                {p.principle_code} — {p.principle_name}
              </option>
            ))}
          </select>
        </div>
        <div className="search">
          <label>Search</label>
          <input
            type="text"
            placeholder="Search documents, sections, uploader"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* File List */}
      <div className="files-list">
        {loading ? (
          <div className="loading-state">Loading evidence files…</div>
        ) : filteredFiles.length === 0 ? (
          <div className="empty-state">No evidence files found.</div>
        ) : (
          filteredFiles.map((file, index) => (
            <div key={`${file.response_id}-${index}`} className="file-card">
              <div className="file-header">

                <div className="file-info">
                  <div className="file-icon">
                    <i className="fa-solid fa-file" />
                  </div>
                  <div className="file-text">
                    <div className="file-name">{getFileName(file.file_name)}</div>
                    <div className="file-section">
                      {file.principle_code} — {file.principle_name}
                    </div>
                  </div>
                </div>

                <div className="file-user">
                  <div className="uploader-name">{file.uploader_name || "—"}</div>
                  <div className="meta-date">{(file.created_at || "").slice(0, 10)}</div>
                </div>

                <div className="file-actions">
                  <span className="comment-count">
                    <i className="fa-regular fa-comment" /> {file.comment_count || 0}
                  </span>
                  {/* <button className="download-btn" onClick={(e) => downloadFile(e, file.evidence_path)}>
                    <i className="fa-solid fa-download" /> Download
                  </button> */}
                </div>

              </div>
            </div>
          ))
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="bottom-nav">
        <span className="back-link" onClick={() => navigate("/dashboard/export")}>
          ← Back to Export
        </span>
        <span className="dashboard-link" onClick={() => navigate("/dashboard")}>
          Go to Dashboard →
        </span>
      </div>

    </div>
  );
}
